import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { toObjectTypeAttributeId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	type OpsgenieTeamAttributeType,
	type AttributeType,
	type ObjectAttributeTypeResponse,
	type InsightObjectAttributeType,
	type DefaultAttributeType,
	type ObjectAttributeType,
	type UserAttributeType,
	type GroupAttributeType,
	type StatusAttributeType,
	type ProjectAttributeType,
	type BitbucketRepoAttributeType,
	AttributeTypeNumbers,
} from '../../types/object-attribute-type.tsx';
import type {
	InsightObjectAttribute,
	DefaultAttribute,
	GroupAttribute,
	ObjectAttribute,
	ProjectAttribute,
	BitbucketRepoAttribute,
	StatusAttribute,
	UserAttribute,
	OpsgenieTeamAttribute,
} from '../../types/object-attribute.tsx';
import type {
	InsightObjectDetailsResponse,
	ObjectAttributeResponse,
	InsightObjectDetails,
} from '../../types/object-details.tsx';
import { matchAttributeType } from '../type-matchers/index.tsx';

export const toAttributeType = (attributeTypeAsNumber: AttributeTypeNumbers): AttributeType => {
	switch (attributeTypeAsNumber) {
		case AttributeTypeNumbers.default:
			return 'default';
		case AttributeTypeNumbers.object:
			return 'object';
		case AttributeTypeNumbers.user:
			return 'user';
		case AttributeTypeNumbers.group:
			return 'group';
		case AttributeTypeNumbers.project:
			return 'project';
		case AttributeTypeNumbers.status:
			return 'status';
		case AttributeTypeNumbers.bitbucketRepo:
			return 'bitbucketRepo';
		case AttributeTypeNumbers.opsgenieTeam:
			return 'opsgenieTeam';
		default: {
			const attributeTypeAsNumberExhaustive: never = attributeTypeAsNumber;
			const error = new Error(
				`Invalid attribute type provided from Insight service found: ${attributeTypeAsNumberExhaustive}`,
			);
			fireCmdbErrorAnalytics({
				meta: {
					id: 'invalid.attribute.type',
					packageName: 'jiraServicedeskInsightObjectTypes',
					teamName: 'aurum',
				},
				error,
			});
			throw error;
		}
	}
};

type IdType = 'attributeTypeId' | 'objectId';

const assertDefined = <T,>(value: T, id: string, idType: IdType): NonNullable<T> => {
	if (value != null) return value;

	const from =
		idType === 'attributeTypeId'
			? 'normalizeAttributeTypeResponse'
			: 'normalizeObjectAttributeResponse';

	const attributes = fg('assets-assertdefined-extra-error-details')
		? {
				id,
				idType,
				from,
			}
		: {};

	const errorMessage = fg('assets-assertdefined-extra-error-details')
		? `Expected value from Insight service is null or undefined from ${from} with id ${id} and idType ${idType}`
		: 'Expected value from Insight service is null or undefined';

	fireCmdbErrorAnalytics({
		meta: {
			id: 'missing.property.from.insight.service',
			packageName: 'jiraServicedeskInsightObjectTypes',
		},
		attributes,
	});

	throw new Error(errorMessage);
};

export const normalizeAttributeTypeResponse = ({
	type,
	defaultType,
	suffix,
	regexValidation,
	referenceType,
	referenceObjectType,
	...attributeType
}: ObjectAttributeTypeResponse): InsightObjectAttributeType =>
	matchAttributeType<InsightObjectAttributeType>(toAttributeType(type), {
		default: (): DefaultAttributeType => ({
			...attributeType,
			defaultType: assertDefined(defaultType, attributeType.id, 'attributeTypeId'),
			suffix,
			regexValidation,
			type: 'default',
		}),
		object: (): ObjectAttributeType => ({
			...attributeType,
			referenceType: assertDefined(referenceType, attributeType.id, 'attributeTypeId'),
			referenceObjectType: assertDefined(referenceObjectType, attributeType.id, 'attributeTypeId'),
			type: 'object',
		}),
		user: (): UserAttributeType => ({
			...attributeType,
			type: 'user',
		}),
		group: (): GroupAttributeType => ({
			...attributeType,
			type: 'group',
		}),
		project: (): ProjectAttributeType => ({
			...attributeType,
			type: 'project',
		}),
		status: (): StatusAttributeType => ({
			...attributeType,
			type: 'status',
		}),
		bitbucketRepo: (): BitbucketRepoAttributeType => ({
			...attributeType,
			type: 'bitbucketRepo',
		}),
		opsgenieTeam: (): OpsgenieTeamAttributeType => ({
			...attributeType,
			type: 'opsgenieTeam',
		}),
	});
export const normalizeAttributeTypesResponse = (attributeTypes: ObjectAttributeTypeResponse[]) =>
	attributeTypes.map<InsightObjectAttributeType>((objectTypeAttributeResponse) =>
		normalizeAttributeTypeResponse(objectTypeAttributeResponse),
	);

export const normalizeObjectAttributeResponse = (
	attributeResponse: ObjectAttributeResponse,
): InsightObjectAttribute => {
	const { objectId, workspaceId, ...rest } = attributeResponse;
	const { objectTypeAttributeId, objectTypeAttribute, objectAttributeValues } = rest;

	const attributeCommon = {
		objectTypeAttributeId: toObjectTypeAttributeId(objectTypeAttributeId),
	};

	return matchAttributeType<InsightObjectAttribute>(
		toAttributeType(attributeResponse.objectTypeAttribute.type),
		{
			default: (): DefaultAttribute => {
				const { type, ...withoutNumberType } = objectTypeAttribute;
				return {
					...attributeCommon,
					type: 'default',
					objectTypeAttribute: {
						...withoutNumberType,
						type: 'default',
						defaultType: assertDefined(objectTypeAttribute.defaultType, objectId, 'objectId'),
					},
					defaultTypeValues: objectAttributeValues.map((attributeValue) => {
						const { referencedObject, group, project, user, status, ...attrValue } = attributeValue;
						return {
							...attrValue,
							value: attrValue.value,
						};
					}),
				};
			},
			object: (): ObjectAttribute => {
				const { type, ...withoutNumberType } = objectTypeAttribute;
				return {
					...attributeCommon,
					type: 'object',
					objectTypeAttribute: {
						...withoutNumberType,
						type: 'object',
						referenceType: assertDefined(objectTypeAttribute.referenceType, objectId, 'objectId'),
						referenceObjectType: assertDefined(
							objectTypeAttribute.referenceObjectType,
							objectId,
							'objectId',
						),
					},
					objectTypeValues: objectAttributeValues.map((attributeValue) => {
						const { bitbucketRepo, group, project, user, status, value, ...attrValue } =
							attributeValue;
						return {
							...attrValue,
							referencedObject: assertDefined(attrValue.referencedObject, objectId, 'objectId'),
						};
					}),
				};
			},
			user: (): UserAttribute => {
				const { type, ...withoutNumberType } = objectTypeAttribute;
				return {
					...attributeCommon,
					type: 'user',
					objectTypeAttribute: { ...withoutNumberType, type: 'user' },
					userTypeValues: objectAttributeValues.map((attributeValue) => {
						const { bitbucketRepo, referencedObject, project, group, status, value, ...attrValue } =
							attributeValue;
						return {
							...attrValue,
							user: assertDefined(attrValue.user, objectId, 'objectId'),
						};
					}),
				};
			},
			group: (): GroupAttribute => {
				const { type, ...withoutNumberType } = objectTypeAttribute;
				return {
					...attributeCommon,
					type: 'group',
					objectTypeAttribute: { ...withoutNumberType, type: 'group' },
					groupTypeValues: objectAttributeValues.map((attributeValue) => {
						const { bitbucketRepo, referencedObject, project, user, status, value, ...attrValue } =
							attributeValue;
						return {
							...attrValue,
							group: assertDefined(attrValue.group, objectId, 'objectId'),
						};
					}),
				};
			},
			project: (): ProjectAttribute => {
				const { type, ...withoutNumberType } = objectTypeAttribute;
				return {
					...attributeCommon,
					type: 'project',
					objectTypeAttribute: { ...withoutNumberType, type: 'project' },
					projectTypeValues: objectAttributeValues.map((attributeValue) => {
						const { bitbucketRepo, referencedObject, user, group, status, value, ...attrValue } =
							attributeValue;
						return {
							...attrValue,
							project: assertDefined(attrValue.project, objectId, 'objectId'),
						};
					}),
				};
			},
			status: (): StatusAttribute => {
				const { type, ...withoutNumberType } = objectTypeAttribute;
				return {
					...attributeCommon,
					type: 'status',
					objectTypeAttribute: { ...withoutNumberType, type: 'status' },
					statusTypeValues: objectAttributeValues.map((attributeValue) => {
						const { bitbucketRepo, referencedObject, user, group, project, value, ...attrValue } =
							attributeValue;
						return {
							...attrValue,
							status: assertDefined(attrValue.status, objectId, 'objectId'),
						};
					}),
				};
			},
			bitbucketRepo: (): BitbucketRepoAttribute => {
				const { type, ...withoutNumberType } = objectTypeAttribute;
				return {
					...attributeCommon,
					type: 'bitbucketRepo',
					objectTypeAttribute: { ...withoutNumberType, type: 'bitbucketRepo' },
					bitbucketRepoTypeValues: objectAttributeValues.map((attributeValue) => {
						const { referencedObject, user, group, project, value, ...attrValue } = attributeValue;
						return {
							...attrValue,
							bitbucketRepo: assertDefined(attrValue.bitbucketRepo, objectId, 'objectId'),
						};
					}),
				};
			},
			opsgenieTeam: (): OpsgenieTeamAttribute => {
				const { type, ...withoutNumberType } = objectTypeAttribute;
				return {
					...attributeCommon,
					type: 'opsgenieTeam',
					objectTypeAttribute: { ...withoutNumberType, type: 'opsgenieTeam' },
					opsgenieTeamTypeValues: objectAttributeValues.map((attributeValue) => {
						const { referencedObject, user, group, project, value, ...attrValue } = attributeValue;
						return {
							...attrValue,
							opsgenieTeam: assertDefined(attrValue.opsgenieTeam, objectId, 'objectId'),
						};
					}),
				};
			},
		},
	);
};

export const normalizeObjectDetailsResponse = (
	objectDetails: InsightObjectDetailsResponse,
): InsightObjectDetails => {
	const normalisedAttributes = objectDetails.attributes.map((attributeResponse) =>
		normalizeObjectAttributeResponse(attributeResponse),
	);

	return {
		...objectDetails,
		attributes: normalisedAttributes,
	};
};
